import { useEffect } from 'react';
import { useQuery } from 'react-query';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';

import { MaintenanceView } from 'src/sections/maintenance/view';

import { getPublicWebsiteSetting } from '../services/settings';

type Props = {
  children: React.ReactNode;
};

const STAFF_ROUTE_PREFIXES = [
  '/administrator',
  '/manage',
  '/teacher',
  '/auth',
  '/500',
  '/404',
  '/403',
];

const isStaffOrSystemRoute = (pathname: string) =>
  STAFF_ROUTE_PREFIXES.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`));

export function WebsiteRuntime({ children }: Props) {
  const location = useLocation();
  const { data } = useQuery(['settings', 'website-public'], getPublicWebsiteSetting, {
    retry: false,
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    if (!data?.faviconUrl) return;

    const selector = "link[rel='icon'], link[rel='shortcut icon']";
    let favicon = document.querySelector<HTMLLinkElement>(selector);

    if (!favicon) {
      favicon = document.createElement('link');
      favicon.rel = 'icon';
      document.head.appendChild(favicon);
    }

    favicon.href = data.faviconUrl;
  }, [data?.faviconUrl]);

  const shouldShowMaintenance =
    Boolean(data?.maintenanceMode) && !isStaffOrSystemRoute(location.pathname);

  if (shouldShowMaintenance) {
    return (
      <>
        <Helmet>
          <title>{data?.siteName || 'LMS'} - Bảo trì</title>
        </Helmet>
        <MaintenanceView
          title={data?.siteName || 'Website đang bảo trì'}
          message={data?.maintenanceMessage || 'Website đang được bảo trì. Vui lòng quay lại sau.'}
          showHomeButton={false}
        />
      </>
    );
  }

  return (
    <>
      <Helmet>
        {data?.siteName && <meta name="application-name" content={data.siteName} />}
        {data?.faviconUrl && <link rel="icon" href={data.faviconUrl} />}
      </Helmet>
      {children}
    </>
  );
}
